import { Clickable } from '@carvana/merch-components';
import React from 'react';
import DefaultSuggestions from './DefaultSuggestions/DefaultSuggestions';
import NoResults from './NoResults';
import { SearchBarWrapper } from './SearchBar.styles';
import SearchBarData from './SearchBarData';
import SearchBarInput from './SearchBarInput';
import Suggestions from './Suggestions';
import { SearchBarProps } from '../context/constants';
import { InputContextProvider, useInputContext } from '../context/InputContext';
import useSearchBar from '../hooks/useSearchBar';
import {
  SearchBarContextProvider,
  useSearchBarContext
} from '../SearchBarContext';
import { SuggestionsContextProvider } from '../SuggestionsContext';

function SearchBarModalContent() {
  const { inputRef, inputValue, noResults } = useInputContext();
  const { onCloseModal } = useSearchBarContext();
  const {
    getInputProps,
    getItemProps,
    getMenuProps,
    handleClearText,
    handleDefaultSuggestionClicked,
    handleInputOnChange,
    handleInputOnFocus,
    handleKeyDown,
    handleRemoveRecentSearch,
    handleSuggestionClick,
    hasSuggestionQuery,
    highlightedIndex,
    setHighlightedIndex,
    suggestionResults
  } = useSearchBar();

  return (
    <form
      action="."
      className="relative h-full overflow-y-auto bg-white"
      data-qa="search-bar-container"
    >
      <div className="sticky top-0 border-b border-grey-2 bg-white p-16">
        <div className="flex gap-16">
          <SearchBarWrapper className="h-44 rounded-[3px] bg-grey-1">
            <SearchBarInput
              focusOnInitialRender
              getInputProps={getInputProps}
              handleClearText={handleClearText}
              handleInputOnChange={handleInputOnChange}
              handleInputOnFocus={handleInputOnFocus}
              handleKeyDown={handleKeyDown}
            />
          </SearchBarWrapper>
          <Clickable
            className="m-auto text-grey-5 underline tl-link-s"
            data-analytics-id="Search Bar - Cancel"
            data-analytics-attributes={JSON.stringify({
              textEntered: inputValue
            })}
            data-qa="Search Bar - Cancel"
            onClick={() => {
              // blur input on close to prevent browser scrolling input in view
              inputRef.current?.blur();
              onCloseModal?.();
            }}
          >
            Cancel
          </Clickable>
        </div>
      </div>
      <ul
        {...getMenuProps({
          onMouseLeave: () => setHighlightedIndex(-1)
        })}
        className="flex flex-col gap-16 px-0 py-16"
      >
        {noResults && hasSuggestionQuery ? (
          <div className="px-16">
            <NoResults />
          </div>
        ) : (
          <Suggestions
            className="px-16"
            getItemProps={getItemProps}
            handleRemoveRecentSearch={handleRemoveRecentSearch}
            handleSuggestionClick={handleSuggestionClick}
            hasInput={hasSuggestionQuery}
            highlightedIndex={highlightedIndex}
            setHighlightedIndex={setHighlightedIndex}
            suggestionResults={suggestionResults}
          />
        )}
        {hasSuggestionQuery && suggestionResults.length ? null : (
          <DefaultSuggestions
            inModal
            handleDefaultSuggestionClicked={handleDefaultSuggestionClicked}
          />
        )}
      </ul>
    </form>
  );
}

export default function SearchBarModalContentContainer(props: SearchBarProps) {
  const { inputValue, setInputValue, ...searchBarContextProps } = props;

  return (
    <SearchBarContextProvider {...searchBarContextProps}>
      <SuggestionsContextProvider>
        <InputContextProvider
          inputValue={inputValue}
          setInputValue={setInputValue}
        >
          <SearchBarData />
          <div data-package="search-bar">
            <SearchBarModalContent />
          </div>
        </InputContextProvider>
      </SuggestionsContextProvider>
    </SearchBarContextProvider>
  );
}
